// markup/MarkupTotalsPanel.jsx — DropPin take-off TOTALS read-out.
// ---------------------------------------------------------------------------
// The quantity-surveying panel: rolls up every MEASURE mark on the drawing into
// a total length + total area, broken down by label/type, live as marks change
// and respecting the current calibration + unit. Pure presentation over
// computeMarkupTakeoff (../takeoff), which in turn uses the vendored
// computeTakeOffTotals — no measurement maths here. Styling uses the shared
// suite tokens (theme.js / tokens.css) so it matches DropPin's other surfaces
// rather than introducing a bespoke look.
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { theme } from '../theme';
import { Ruler, Square, Sigma, X, TriangleAlert } from 'lucide-react';
import { computeMarkupTakeoff } from './takeoff.mjs';

export default function MarkupTotalsPanel({ m, naturalW, naturalH, onClose, markTypes }) {
  const W = naturalW || 1;
  const H = naturalH || 1;

  // 2026-08-14 — key -> the tenant's CURRENT name, so renaming a type in
  // Settings renames it in the totals too. Built here rather than stored on the
  // mark: the mark keeps the immutable key, the label stays editable.
  const typeNames = useMemo(() => {
    const out = {};
    for (const t2 of (markTypes || [])) if (t2 && t2.key) out[t2.key] = t2.label;
    return out;
  }, [markTypes]);

  const t = useMemo(
    () => computeMarkupTakeoff(m.objects, W, H, m.calibration, m.unit, 'auto', typeNames),
    [m.objects, W, H, m.calibration, m.unit, typeNames],
  );

  const hasMeasures = t.length.count > 0 || t.area.count > 0;

  const wrap = {
    position: 'absolute', bottom: 8, left: 8, zIndex: 20, width: 250, maxWidth: 'calc(100% - 16px)',
    background: theme.surface, border: `1px solid ${theme.border}`,
    borderRadius: 10, boxShadow: 'var(--shadow-3)', overflow: 'hidden',
    fontFamily: 'inherit', color: theme.text,
  };
  const head = {
    display: 'flex', alignItems: 'center', gap: 7, padding: '9px 10px',
    borderBottom: `1px solid ${theme.border}`, fontWeight: 800, fontSize: 12.5, letterSpacing: 0.2,
  };
  const body = { padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 7 };
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12.5 };
  const rowLeft = { display: 'flex', alignItems: 'center', gap: 7, color: theme.textLight, minWidth: 0 };
  const val = { fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const countPill = {
    fontSize: 10.5, fontWeight: 700, color: theme.textLight,
    background: 'var(--surface-1)', border: `1px solid ${theme.border}`,
    borderRadius: 999, padding: '0 6px', lineHeight: '16px',
  };
  const closeBtn = {
    marginLeft: 'auto', width: 22, height: 22, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', border: 'none', background: 'transparent', color: theme.textLight,
    cursor: 'pointer', borderRadius: 6, padding: 0,
  };
  const nameStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <div style={wrap} onPointerDown={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()}>
      <div style={head}>
        <Sigma size={15} strokeWidth={2.4} color={theme.brand} />
        <span>Take-off totals</span>
        {onClose && (
          <button type="button" style={closeBtn} title="Hide totals" aria-label="Hide totals" onClick={onClose}>
            <X size={15} strokeWidth={2.2} />
          </button>
        )}
      </div>

      <div style={body}>
        {!hasMeasures ? (
          <div style={{ fontSize: 12, color: theme.textLight, lineHeight: 1.5, padding: '2px 0' }}>
            Draw with the <strong>Measure</strong> tool to see length + area totals here.
          </div>
        ) : (
          <>
            {!t.calibrated && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, lineHeight: 1.4,
                color: 'var(--warning-700, #92400e)', background: 'var(--warning-50, #fffbeb)',
                border: '1px solid var(--warning-200, #fde68a)', borderRadius: 7, padding: '6px 8px',
              }}>
                <TriangleAlert size={13} strokeWidth={2.2} style={{ flex: '0 0 auto', marginTop: 1 }} />
                <span>No scale set — showing pixels. Use <strong>Set scale</strong> for real units.</span>
              </div>
            )}

            {/* Per label/type breakdown (live) */}
            {t.groups.map((g) => (
              <div key={g.key} style={row}>
                <span style={rowLeft}>
                  {g.kind === 'area'
                    ? <Square size={14} strokeWidth={2.1} style={{ flex: '0 0 auto' }} />
                    : <Ruler size={14} strokeWidth={2.1} style={{ flex: '0 0 auto' }} />}
                  <span style={nameStyle} title={g.key}>{g.key}</span>
                  <span style={countPill}>{g.count}</span>
                </span>
                <span style={val}>{g.label}</span>
              </div>
            ))}

            {/* Grand totals */}
            <div style={{ borderTop: `1px solid ${theme.border}`, margin: '2px 0 0', paddingTop: 7, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {t.length.count > 0 && (
                <div style={row}>
                  <span style={{ ...rowLeft, color: theme.text, fontWeight: 700 }}>Total length</span>
                  <span style={val}>{t.length.label}</span>
                </div>
              )}
              {t.area.count > 0 && (
                <div style={row}>
                  <span style={{ ...rowLeft, color: theme.text, fontWeight: 700 }}>Total area</span>
                  <span style={val}>{t.area.label}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
